import * as React from 'react';

import {
  DataGridColumn,
  cn,
  humanReadableNumber,
  humanReadableShortPricingUnit,
} from '@rematter/ui-web';

import { ItemPositionRecord } from '../../../types/position';

import { FacilityRecord } from '~/modules/trade/_graphql/queryFacilities';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { getLocale } from '~/shared/utils/locale';

export type CommonCellProps = {
  className: string;
  onClick?: () => void;
  onDoubleClick?: () => void;
};

type Args = {
  facilities?: FacilityRecord[];
};

const formatNumber = (value: number) =>
  humanReadableNumber(value, { locale: getLocale(), decimalPlaces: 2 });

export const usePositionsListColumns = ({
  facilities = [],
}: Args): DataGridColumn<ItemPositionRecord>[] => {
  const t = useTranslations();

  return React.useMemo(() => {
    const getFacilityName = (facilityId?: string) => {
      if (!facilityId) return t.positionsView.table.noFacility;
      const facility = facilities.find((f) => f.id === facilityId);
      return facility?.facilityName || facilityId;
    };

    const columns: DataGridColumn<ItemPositionRecord>[] = [
      {
        key: 'item',
        header: t.positionsView.table.columns.item,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">
              {row.itemName || `Item ${row.itemId}`}
            </span>
            {row.itemSku && (
              <span className="text-xs text-neutral/60">{row.itemSku}</span>
            )}
          </div>
        ),
        width: 200,
        sortable: true,
      },
      {
        key: 'facility',
        header: t.positionsView.table.columns.facility,
        cell: ({ row }) => (
          <span className={!row.facilityId ? 'text-neutral/60 italic' : ''}>
            {row.facilityName || getFacilityName(row.facilityId)}
          </span>
        ),
        width: 150,
      },
      {
        key: 'currentInventory',
        header: t.positionsView.table.columns.currentInventory,
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatNumber(row.currentInventory)}{' '}
            {humanReadableShortPricingUnit(row.unit)}
          </span>
        ),
        width: 120,
      },
      {
        key: 'incoming',
        header: t.positionsView.table.columns.incoming,
        cell: ({ row }) => (
          <span className="text-success tabular-nums">
            +{formatNumber(row.incoming)}{' '}
            {humanReadableShortPricingUnit(row.unit)}
          </span>
        ),
        width: 120,
        sortable: true,
      },
      {
        key: 'outgoing',
        header: t.positionsView.table.columns.outgoing,
        cell: ({ row }) => (
          <span className="text-failure tabular-nums">
            -{formatNumber(row.outgoing)}{' '}
            {humanReadableShortPricingUnit(row.unit)}
          </span>
        ),
        width: 120,
        sortable: true,
      },
      {
        key: 'netPosition',
        header: t.positionsView.table.columns.netPosition,
        cell: ({ row }) => (
          <span
            className={cn('font-semibold tabular-nums', {
              'text-failure': row.netPosition < 0,
              'text-success': row.netPosition > 0,
              'text-neutral/60': row.netPosition === 0,
            })}
          >
            {row.netPosition >= 0 ? '+' : ''}
            {formatNumber(row.netPosition)}{' '}
            {humanReadableShortPricingUnit(row.unit)}
          </span>
        ),
        width: 130,
        sortable: true,
      },
    ];

    return columns;
  }, [t, facilities]);
};
